const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.chatCode = null;
    this.token = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 15;
    this.intentionalClose = false;

    // Callbacks
    this._onConnected = null;
    this._onStreamStart = null;
    this._onStreamToken = null;
    this._onStreamEnd = null;
    this._onStatus = null;
    this._onError = null;
  }

  connect(chatCode, token) {
    // Guard: don't reconnect if already connected to same session
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.chatCode === chatCode) {
      console.log('[WS] Already connected to', chatCode);
      return;
    }

    this.chatCode = chatCode;
    this.token = token;
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    this._createConnection();
  }

  _createConnection() {
    if (this.ws) {
      // Mark as intentional so onclose doesn't trigger reconnect
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }

    const url = `${WS_URL}?chatCode=${this.chatCode}&token=${this.token}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('[WS] Connected');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this._handleMessage(data);
      } catch (err) {
        console.error('[WS] Parse error:', err);
      }
    };

    this.ws.onclose = () => {
      console.log('[WS] Disconnected');
      if (!this.intentionalClose) {
        this._attemptReconnect();
      }
    };

    this.ws.onerror = (err) => {
      console.error('[WS] Error:', err);
    };
  }

  _handleMessage(data) {
    switch (data.type) {
      case 'connected':
        if (this._onConnected) this._onConnected(data);
        break;
      case 'stream_start':
        if (this._onStreamStart) this._onStreamStart(data);
        break;
      case 'stream_token':
        if (this._onStreamToken) this._onStreamToken(data.content);
        break;
      case 'stream_end':
        if (this._onStreamEnd) this._onStreamEnd(data);
        break;
      case 'status':
        if (this._onStatus) this._onStatus(data.status);
        break;
      case 'error':
        if (this._onError) this._onError(data.message);
        break;
      default:
        console.log('[WS] Unknown message type:', data.type);
    }
  }

  _attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WS] Max reconnect attempts reached');
      if (this._onError) this._onError('Connection lost. Please reload the page.');
      return;
    }
    this.reconnectAttempts++;
    const delay = Math.min(2000 * Math.pow(1.3, this.reconnectAttempts - 1), 15000);
    console.log(`[WS] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => {
      if (!this.intentionalClose && this.chatCode && this.token) {
        this._createConnection();
      }
    }, delay);
  }

  sendTranscript(content) {
    this._send({ type: 'transcript', content });
  }

  sendImage(content, imageUrl) {
    this._send({ type: 'image', content, imageUrl });
  }

  sendTextFile(content, filename, fileContent) {
    this._send({ type: 'textfile', content, filename, fileContent });
  }

  _send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[WS] Not connected, cannot send');
      if (this._onError) this._onError('Not connected to server. Reconnecting...');
    }
  }

  // Event setters
  onConnected(cb) { this._onConnected = cb; }
  onStreamStart(cb) { this._onStreamStart = cb; }
  onStreamToken(cb) { this._onStreamToken = cb; }
  onStreamEnd(cb) { this._onStreamEnd = cb; }
  onStatus(cb) { this._onStatus = cb; }
  onError(cb) { this._onError = cb; }

  disconnect() {
    this.intentionalClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.chatCode = null;
    this.token = null;
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

const wsService = new WebSocketService();
export default wsService;
